import {
	type EventMedalHistoryItem,
	type EventGender,
	type Keyset,
	MEDAL_EVENT_STATES,
	type MedalDaily,
	type MedalEventList,
	type MedalEventSort,
	type MedalEventState,
	type MedalReviewOrder,
	type MedalTally,
	type MedalTallyBreakdown,
} from "@seagames/contracts";
import { infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/react-query";
import { api } from "../api/client";
import { retryTransient } from "../api/queries";

/**
 * The medal board's reads — every key under `["medals", …]`, so a desk write's
 * `refreshAfterDeskWrite` refreshes them all. Each is ONE bounded request:
 * the events table an offset page, the calendar one row per day, the tally
 * one cached aggregate, a podium's history a keyset page. React Query pauses
 * the polls while the tab is in the background (`refetchIntervalInBackground`
 * stays off).
 */

/** Board events and the run sheet: a meet made official in OVR moves them with no desk write. */
export const BOARD_EVENTS_POLL_MS = 30_000;
/** Progress, the calendar and the tally. */
export const BOARD_POLL_MS = 60_000;

export const boardKeys = {
	events: (params: BoardEventsParams) => ["medals", "events", params] as const,
	daily: (gamesId: string, from?: string, to?: string) => ["medals", "daily", gamesId, from ?? null, to ?? null] as const,
	tally: (params: BoardTallyParams) => ["medals", "tally", params.games_id, params] as const,
	history: (eventId: string) => ["medals", "history", eventId] as const,
};

export interface BoardEventsParams {
	games_id: string;
	state?: readonly MedalEventState[] | undefined;
	sport_id?: string | undefined;
	responsible_organization_id?: string | undefined;
	q?: string | undefined;
	from?: string | undefined;
	to?: string | undefined;
	sort?: MedalEventSort | undefined;
	page?: number | undefined;
	page_size?: number | undefined;
}

/** `GET /medals/events` — one offset page and the state counts of the same filters (desk + admins). */
export function boardEventsQuery(params: BoardEventsParams, poll = true) {
	return queryOptions({
		queryKey: boardKeys.events(params),
		queryFn: ({ signal }) =>
			api<MedalEventList>("GET", "/medals/events", {
				query: { ...params, state: params.state && params.state.length > 0 ? params.state.join(",") : undefined },
				signal,
			}),
		staleTime: 10_000,
		refetchInterval: poll ? BOARD_EVENTS_POLL_MS : false,
		placeholderData: keepPreviousData,
		retry: retryTransient,
	});
}

/** `GET /medals/daily` — the games day by day: what is due, what was awarded, the firsts. */
export function boardDailyQuery(gamesId: string, from?: string, to?: string) {
	return queryOptions({
		queryKey: boardKeys.daily(gamesId, from, to),
		queryFn: ({ signal }) => api<MedalDaily>("GET", "/medals/daily", { query: { games_id: gamesId, from, to }, signal }),
		staleTime: 20_000,
		refetchInterval: BOARD_POLL_MS,
		retry: retryTransient,
	});
}

export interface BoardTallyParams {
	games_id: string;
	sport_id?: string | undefined;
	gender?: EventGender | undefined;
	from?: string | undefined;
	to?: string | undefined;
	breakdown?: MedalTallyBreakdown | undefined;
}

/** `GET /medals/tally` with the board's variants — one cached aggregate per variant on the API. */
export function boardTallyQuery(params: BoardTallyParams, poll = true) {
	return queryOptions({
		queryKey: boardKeys.tally(params),
		queryFn: ({ signal }) => api<MedalTally>("GET", "/medals/tally", { query: { ...params }, signal }),
		staleTime: 20_000,
		refetchInterval: poll ? BOARD_POLL_MS : false,
		placeholderData: keepPreviousData,
		retry: retryTransient,
	});
}

export const HISTORY_PAGE = 25;

/** `GET /events/:id/medals/history` — newest first, a keyset page per "Show older". */
export function eventHistoryQuery(eventId: string) {
	return infiniteQueryOptions({
		queryKey: boardKeys.history(eventId),
		queryFn: ({ pageParam, signal }) =>
			api<Keyset<EventMedalHistoryItem>>("GET", `/events/${encodeURIComponent(eventId)}/medals/history`, {
				query: { limit: HISTORY_PAGE, cursor: pageParam ?? undefined },
				signal,
			}),
		initialPageParam: null as string | null,
		getNextPageParam: (last) => (last.has_more && last.next_cursor ? last.next_cursor : null),
		staleTime: 10_000,
		retry: retryTransient,
	});
}

/** The review lists' extra keys (`/medals/pending` …): search, sport, grouping. */
export interface ReviewFilters {
	q?: string | undefined;
	sport_id?: string | undefined;
	order?: MedalReviewOrder | undefined;
}

/** `state_counts` as a record, every state present (0 when the API sent none). */
export function stateCountsOf(list: Pick<MedalEventList, "state_counts"> | undefined): Record<MedalEventState, number> {
	const out = Object.fromEntries(MEDAL_EVENT_STATES.map((s) => [s, 0])) as Record<MedalEventState, number>;
	for (const c of list?.state_counts ?? []) out[c.state] = c.count;
	return out;
}
